import { useId, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@sakhtar/design-system/react';
import type { AnswerContext } from './types.js';
import { useModalSheet } from './useModalSheet.js';
import { resolveDocumentRoute } from './documentRoute.ts';

export { resolveDocumentRoute };

export interface ContextPanelProps {
  readonly context: AnswerContext | null;
  readonly open: boolean;
  readonly onToggle: () => void;
  /** Scroll the conversation to the message a path step belongs to. */
  readonly onJumpTo: (messageId: string) => void;
  /**
   * Mobile (frame M3): render as a modal bottom sheet instead of the side
   * column. The sheet exists only while open, `onToggle` closes it.
   */
  readonly sheet?: boolean;
  readonly base?: string | undefined;
}

/**
 * Right-hand (end) column «پنل زمینه»: analysis path (breadcrumbs), current
 * filters, data sources of the newest answer, Excel export. Collapsible.
 *
 * «خروجی Excel» is rendered disabled: export is centralised in the /data
 * route and no per-answer export endpoint exists.
 */
export function ContextPanel({
  context,
  open,
  onToggle,
  onJumpTo,
  sheet = false,
  base,
}: ContextPanelProps) {
  if (sheet) {
    return open ? (
      <ContextSheet context={context} onClose={onToggle} onJumpTo={onJumpTo} base={base} />
    ) : null;
  }
  return (
    <aside className="ai-panel" data-open={open} aria-label="پنل زمینه">
      <div className="ai-panel__head">
        {open ? <span className="ai-panel__title">پنل زمینه</span> : null}
        <button
          type="button"
          className="sk-btn sk-btn--ghost sk-btn--icon sk-btn--sm"
          aria-expanded={open}
          aria-label={open ? 'بستن پنل زمینه' : 'باز کردن پنل زمینه'}
          title={open ? 'بستن پنل زمینه' : 'باز کردن پنل زمینه'}
          onClick={onToggle}
        >
          <Icon name={open ? 'chevronRight' : 'chevronLeft'} size="sm" />
        </button>
      </div>

      {open ? <ContextBody context={context} onJumpTo={onJumpTo} base={base} /> : null}
    </aside>
  );
}

/**
 * Frame M3 - the same panel as a bottom sheet: scrim, handle, title row with a
 * 44px close button. `role="dialog"` + `aria-modal`; focus handling, Esc and the
 * scroll lock live in `useModalSheet`. The «بستن» scrim click closes it too.
 */
function ContextSheet({
  context,
  onClose,
  onJumpTo,
  base,
}: {
  readonly context: AnswerContext | null;
  readonly onClose: () => void;
  readonly onJumpTo: (messageId: string) => void;
  readonly base?: string | undefined;
}) {
  const titleId = useId();
  const sheetRef = useRef<HTMLElement | null>(null);
  useModalSheet(true, sheetRef, onClose);
  return (
    <div className="ai-sheet">
      <div className="sk-drawer-backdrop ai-sheet__scrim" onClick={onClose} aria-hidden="true" />
      <aside
        ref={sheetRef}
        className="ai-panel ai-panel--sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <span className="ai-sheet__handle" aria-hidden="true" />
        <div className="ai-panel__head">
          <h2 id={titleId} className="ai-panel__title">
            پنل زمینه
          </h2>
          <button
            type="button"
            className="sk-btn sk-btn--ghost sk-btn--icon sk-btn--sm"
            aria-label="بستن پنل زمینه"
            onClick={onClose}
          >
            <Icon name="x" size="sm" />
          </button>
        </div>
        <ContextBody context={context} onJumpTo={onJumpTo} base={base} />
      </aside>
    </div>
  );
}

function ContextBody({
  context,
  onJumpTo,
  base,
}: {
  readonly context: AnswerContext | null;
  readonly onJumpTo: (messageId: string) => void;
  readonly base?: string | undefined;
}) {
  const hasContext = context !== null && context.path.length > 0;
  return (
    <div className="ai-panel__body">
      <section className="ai-panel__section">
        <h3 className="ai-panel__heading">مسیر تحلیل</h3>
        {hasContext ? (
          <ol className="ai-path">
            {context.path.map((step, index) => (
              <li
                key={step.id}
                className="ai-path__step"
                style={{ ['--ai-depth' as string]: index }}
              >
                {index > 0 ? (
                  <span className="ai-path__arrow" aria-hidden="true">
                    ←
                  </span>
                ) : null}
                {step.kind === 'record' ? (
                  <span className="ai-record-step">
                    <span
                      className="ai-mono ltr"
                      aria-current={index === context.path.length - 1 ? 'step' : undefined}
                    >
                      {step.label}
                    </span>
                    {resolveDocumentRoute(step.label, base) ? (
                      <Link
                        to={resolveDocumentRoute(step.label, base)!}
                        className="ai-link ai-record-direct-link"
                        title="ورود به سند اصلی در سیستم"
                      >
                        ورود به سند ↗
                      </Link>
                    ) : null}
                  </span>
                ) : !step.messageId ? (
                  <span
                    className="ai-mono ltr"
                    aria-current={index === context.path.length - 1 ? 'step' : undefined}
                  >
                    {step.label}
                  </span>
                ) : (
                  <button
                    type="button"
                    className="ai-link"
                    aria-current={index === context.path.length - 1 ? 'step' : undefined}
                    onClick={() => onJumpTo(step.messageId as string)}
                  >
                    {step.label}
                  </button>
                )}
              </li>
            ))}
          </ol>
        ) : (
          <p className="ai-panel__hint">
            هنوز تحلیلی انجام نشده است. با نخستین پاسخ، مسیر لایه‌ها اینجا ساخته می‌شود.
          </p>
        )}
      </section>

      {hasContext ? (
        <>
          <section className="ai-panel__section">
            <h3 className="ai-panel__heading">فیلترهای فعلی</h3>
            <div className="ai-panel__tags">
              {context.filters.map((filter) => (
                <span key={filter} className="sk-tag">
                  {filter}
                </span>
              ))}
            </div>
          </section>
          <section className="ai-panel__section">
            <h3 className="ai-panel__heading">منابع دادهٔ پاسخ آخر</h3>
            <p className="ai-panel__sources">
              {context.sources.length > 0 ? context.sources.join(' · ') : '—'}
            </p>
          </section>
          <div className="ai-panel__actions">
            <button
              type="button"
              className="sk-btn sk-btn--secondary sk-btn--block"
              disabled
              title="خروجی در نسخهٔ نمایشی فعال نیست"
            >
              <span className="ai-icon-flip">
                <Icon name="upload" size="sm" />
              </span>
              خروجی Excel
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
